import interactionPlugin, { type DateClickArg, type EventResizeDoneArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useCallback, useMemo, useRef } from 'react';

import { getInterviewerColor } from '@/lib/interviewerColors';

import { SlotEventContent } from './SlotEventContent';
import './scheduling-calendar.css';

import type { InterviewSlot } from '@/types/scheduling.types';
import type { DatesSetArg, EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core';

interface SlotCalendarProps {
  slots: InterviewSlot[];
  visibleInterviewerIds: Set<string>;
  selectedSlotId: string | null;
  onDateClick: (date: string, startTime: string, endTime: string) => void;
  onSelect: (startStr: string, endStr: string) => void;
  onEventDrop: (slotId: string, newStart: string, newEnd: string) => void;
  onEventResize: (slotId: string, newStart: string, newEnd: string) => void;
  onEventClick: (slotId: string) => void;
  onDatesSet: (start: string, end: string) => void;
}

function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function SlotCalendar({
  slots,
  visibleInterviewerIds,
  selectedSlotId,
  onDateClick,
  onSelect,
  onEventDrop,
  onEventResize,
  onEventClick,
  onDatesSet,
}: SlotCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null);

  const events: EventInput[] = useMemo(
    () =>
      slots
        .filter((slot) => visibleInterviewerIds.has(slot.interviewerUserId))
        .map((slot) => {
          const color = getInterviewerColor(slot.interviewerUserId);
          const isSelected = slot.slotId === selectedSlotId;
          return {
            id: slot.slotId,
            start: slot.startAtUtc,
            end: slot.endAtUtc,
            backgroundColor: isSelected ? color.hex + '40' : color.hex + '20',
            borderColor: color.hex,
            textColor: color.hex,
            classNames: isSelected ? ['fc-event--selected'] : [],
            extendedProps: {
              interviewerName: slot.interviewerName,
              interviewerUserId: slot.interviewerUserId,
              bookings: slot.bookings,
              capacity: slot.capacity,
            },
          };
        }),
    [slots, visibleInterviewerIds, selectedSlotId],
  );

  const handleDateClick = useCallback(
    (arg: DateClickArg) => {
      const date = arg.date;
      const start = formatTime(date);
      const endDate = new Date(date.getTime() + 30 * 60 * 1000);
      const end = formatTime(endDate);
      onDateClick(formatDateStr(date), start, end);
    },
    [onDateClick],
  );

  const handleSelect = useCallback(
    (arg: { start: Date; end: Date }) => {
      onSelect(arg.start.toISOString(), arg.end.toISOString());
    },
    [onSelect],
  );

  const handleEventDrop = useCallback(
    (arg: EventDropArg) => {
      if (arg.event.start && arg.event.end) {
        onEventDrop(arg.event.id, arg.event.start.toISOString(), arg.event.end.toISOString());
      }
    },
    [onEventDrop],
  );

  const handleEventResize = useCallback(
    (arg: EventResizeDoneArg) => {
      if (arg.event.start && arg.event.end) {
        onEventResize(arg.event.id, arg.event.start.toISOString(), arg.event.end.toISOString());
      }
    },
    [onEventResize],
  );

  const handleEventClick = useCallback(
    (arg: EventClickArg) => {
      onEventClick(arg.event.id);
    },
    [onEventClick],
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      onDatesSet(arg.start.toISOString(), arg.end.toISOString());
    },
    [onDatesSet],
  );

  const handleWindowResize = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const isMobile = window.innerWidth < 768;
    const currentView = api.view.type;
    if (isMobile && currentView !== 'timeGridDay') {
      api.changeView('timeGridDay');
    } else if (!isMobile && currentView !== 'timeGridWeek') {
      api.changeView('timeGridWeek');
    }
  }, []);

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4'>
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin]}
        initialView='timeGridWeek'
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridWeek,timeGridDay',
        }}
        slotMinTime='07:00:00'
        slotMaxTime='20:00:00'
        slotDuration='00:30:00'
        allDaySlot={false}
        editable={true}
        selectable={true}
        selectMirror={true}
        eventOverlap={true}
        slotEventOverlap={true}
        events={events}
        eventContent={(eventInfo) => <SlotEventContent eventInfo={eventInfo} />}
        dateClick={handleDateClick}
        select={handleSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        windowResize={handleWindowResize}
        height='auto'
        expandRows={true}
        nowIndicator={true}
        dayMaxEvents={true}
      />
    </div>
  );
}
